<template>
  <tr class="b2b-grid__product-row">
    <td v-if="!forPrint" class="b2b-grid__cell lg:table-cell" qaid="sku-grid.icon">
      <grid-icon :data="{value: 'card_giftcard'}"></grid-icon>
    </td>

    <td v-if="!forPrint" class="b2b-grid__cell lg:table-cell" qaid="sku-grid.note">
      <grid-note
        v-if="productHasNote"
        :data="{
          value: 'note_add',
          editable: false,
          content: item.lineNote,
          hasNotification: productHasNote,
        }"
        :item-id="item.sku"
        :max-length="60"
        :base-properties="baseProperties"
        @note-added="handleAddNote"
      ></grid-note>
    </td>

    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.split">
      <grid-icon v-if="item.isSplitWarehouse" :data="{value: 'call_split'}"></grid-icon>
    </td>

    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.sku">
      <div class="b2b-grid__label">{{ getHeader('sku') }}</div>
      <div class="b2b-grid__content">
        <div class="flex items-center">
          <a
            :href="`${encodedContextPath}/my-account/orders?partNumber=${item.sku}`"
            class="leading-none mr-1"
            qaid="sku-grid.orderHistory"
          >
            <i class="material-icons">history</i>
          </a>
          <grid-text
            class="flex"
            :data="{
              value: item.sku,
              tooltip: item.mfr ? {MFR: item.mfr} : null,
              link: encodedContextPath + '/p/' + item.sku,
            }"
          ></grid-text>
        </div>
      </div>
    </td>

    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.item">
      <div class="b2b-grid__label">{{ getHeader('item') }}</div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: item.itemName}"></grid-text>
      </div>
    </td>

    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.upc">
      <div class="b2b-grid__label">{{ getHeader('upc') }}</div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: item.ean ? item.ean : item.upc}"></grid-text>
      </div>
    </td>

    <td
      v-if="checkoutStep != 'confirmation'"
      class="b2b-grid__cell lg:table-cell text-left lg:text-right"
      qaid="sku-grid.allocated"
    >
      <div class="b2b-grid__label">{{ getHeader('allocated') }}</div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: item.quantityAllocatedDI == 0 ? null : item.quantityAllocatedDI}"></grid-text>
      </div>
    </td>

    <td
      v-if="checkoutStep != 'confirmation'"
      class="b2b-grid__cell lg:table-cell text-left lg:text-right"
      qaid="sku-grid.backordered"
    >
      <div class="b2b-grid__label">{{ getHeader('backordered') }}</div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: item.quantityBackorderedDI == 0 ? null : item.quantityBackorderedDI}"></grid-text>
      </div>
    </td>

    <td
      v-if="!consumerFriendlyMode"
      class="b2b-grid__cell lg:table-cell text-left lg:text-right"
      qaid="sku-grid.order-price"
    >
      <div class="b2b-grid__label">{{ getHeader('price') }}</div>
      <div class="b2b-grid__content">
        <grid-price :data="{dealerCost: item.price.dealerCost, discount: discount}"></grid-price>
      </div>
    </td>

    <td v-if="checkoutStep != 'confirmation'" qaid="sku-grid.list" class="b2b-grid__cell lg:table-cell lg:text-right">
      <div class="b2b-grid__label"><i class="md-18">list</i></div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: item.skuQtyInSavedList > 0 ? item.skuQtyInSavedList : ''}"></grid-text>
      </div>
    </td>

    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.qty">
      <div class="b2b-grid__label">{{ getHeader('quantity') }}</div>
      <div class="b2b-grid__content">
        <grid-text v-if="!isBackorder" key="grid-qty" :data="{value: item.nonBackorderedQty}"></grid-text>
        <grid-text v-else key="grid-qty" :data="{value: item.backorderedQty}"></grid-text>
      </div>
    </td>

    <td v-if="!consumerFriendlyMode" class="b2b-grid__cell lg:table-cell text-right" qaid="sku-grid.subtotal">
      <div class="b2b-grid__label">{{ getHeader('subtotal') }}</div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: item.formattedLineTotal}"></grid-text>
      </div>
    </td>
  </tr>
</template>

<script>
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import {mapState, mapGetters} from 'vuex';
import GridIcon from '@/components/grid/GridIcon';
import GridNote from '@/components/grid/GridNote';
import GridText from '@/components/grid/GridText';
import GridPrice from '@/components/grid/GridPrice';

export default {
  components: {GridIcon, GridNote, GridText, GridPrice},

  props: {
    item: {
      type: Object,
      default: () => {},
    },
    headers: {
      type: Array,
      default: () => [],
    },
    isBackorder: Boolean,
    forPrint: {
      type: Boolean,
      default: false,
    },
    checkoutStep: {
      type: String,
      default: '',
    },
    baseProperties: {
      type: Array,
      default: () => [],
      required: false,
    },
  },

  computed: {
    productHasNote() {
      return this.item && !isEmpty(this.item.lineNote);
    },

    discount() {
      if (this.item && this.item.price) {
        if (this.item.price.orderedPrice) {
          return this.item.price.orderedPrice;
        } else if (this.advancedOrderingMode && this.item.price.advancedPrice) {
          return this.item.price.advancedPrice;
        } else if (this.item.price.sale && Number(this.item.price.sale.replace(/[^0-9.-]+/g, '')) > 0) {
          return this.item.price.sale;
        }
      }

      return 0;
    },

    ...mapState('advancedOrdering', ['advancedOrderingMode']),
    ...mapState('backend', ['encodedContextPath']),
    ...mapGetters('backend', ['consumerFriendlyMode']),
  },

  methods: {
    getHeader(headerName) {
      let header = find(this.headers, (header) => header.name === headerName);
      return header ? header.label : '';
    },

    handleAddNote(event) {
      console.log(event);
    },
  },
};
</script>
